import { useState } from 'react'

/**
 * Brand and hero visuals for the console's entrance.
 *
 * **The SAIL mark is a slot, not a drawing.** SAIL's logo is a registered trademark and the official artwork
 * is not in this repository. Reproducing it from memory would produce something subtly wrong — wrong
 * proportions, wrong blue, wrong device — and passing that off as the corporate mark is worse than not
 * showing one, because it would end up on a plant-floor screen and in screenshots. So [SailMark] loads
 * `/console/brand/sail-logo.svg` (or `.png`) if an administrator has dropped the official file there, and
 * falls back to a neutral typographic lockup until then. Drop the real asset in and it appears everywhere,
 * with no code change.
 *
 * **The hero is SVG, not a photograph.** A stock "Industry 4.0" photo would be a licensing question, a
 * 300 kB download on a plant PC, and a poor match for a screen whose entire palette is specified. Drawn
 * geometry scales to any display, costs nothing, and can use the exact SAIL tokens — so the entrance looks
 * deliberate rather than decorated.
 */

/* ---- SAIL mark ---------------------------------------------------------- */

export function SailMark({ height = 34, showWordmark = true }) {
  const [missing, setMissing] = useState(false)

  if (!missing) {
    return (
      <img
        src="/console/brand/sail-logo.svg"
        alt="SAIL — Steel Authority of India Limited"
        style={{ height, width: 'auto', display: 'block' }}
        // No official asset yet: fall through to the lockup below rather than showing a broken-image icon.
        onError={() => setMissing(true)}
      />
    )
  }

  return (
    <span className="sail-lockup" style={{ ['--mark-h']: `${height}px` }}>
      <span className="sail-lockup-mark" aria-hidden="true">
        {/*
          A neutral steel-industry glyph, deliberately NOT an imitation of the SAIL device: three stacked
          plates suggesting rolled steel, in the brand blue. It reads as "this is the plant's system" without
          claiming to be the corporate mark.
        */}
        <svg viewBox="0 0 32 32" role="img">
          <rect x="4" y="6" width="24" height="4.5" rx="1.4" fill="currentColor" opacity=".95" />
          <rect x="7" y="13.5" width="18" height="4.5" rx="1.4" fill="currentColor" opacity=".7" />
          <rect x="10" y="21" width="12" height="4.5" rx="1.4" fill="currentColor" opacity=".45" />
        </svg>
      </span>
      {showWordmark && (
        <span className="sail-lockup-text">
          <strong>SAIL</strong>
          <em>Bokaro Steel Plant</em>
        </span>
      )}
    </span>
  )
}

/* ---- Industry 4.0 hero -------------------------------------------------- */

/**
 * The entrance visual: an EOT crane over a bay, with a sensor mesh above it.
 *
 * Every element earns its place rather than being ornament. The crane is the asset this system exists for,
 * drawn as the girder-and-hoist silhouette an operator would recognise. The nodes and links above it are the
 * Industry 4.0 idea made literal — instrumented machines reporting to something that reasons about them,
 * which is exactly what the dashboard behind this screen does. The pulse travels along the hoist rope,
 * because that is where the condition readings actually come from.
 *
 * Motion is slow and stops entirely under `prefers-reduced-motion`. A login screen that throbs is a login
 * screen people learn to resent.
 */
export function IndustryHero() {
  return (
    <svg
      className="hero-art"
      viewBox="0 0 520 420"
      role="img"
      aria-label="Illustration of an overhead travelling crane connected to a network of condition sensors"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        {/*
          A gradient here is within the rules. CLAUDE.md rule 7 bans gradients on **data-bearing surfaces** —
          nothing is read over this, no status and no metric — and the reasoning it gives (contrast over a
          gradient is unverifiable) does not apply to a decorative panel with no text on it.
        */}
        <linearGradient id="heroSky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0A4E8A" />
          <stop offset="55%" stopColor="#075EA8" />
          <stop offset="100%" stopColor="#00617A" />
        </linearGradient>
        <linearGradient id="beamGlow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#55D6F1" stopOpacity="0" />
          <stop offset="50%" stopColor="#55D6F1" stopOpacity=".9" />
          <stop offset="100%" stopColor="#55D6F1" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="nodeGlow">
          <stop offset="0%" stopColor="#7FE7FB" stopOpacity=".55" />
          <stop offset="100%" stopColor="#7FE7FB" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="520" height="420" fill="url(#heroSky)" />

      {/* Bay structure: the columns and roof trusses of a steel shop, kept faint so the crane reads first. */}
      <g stroke="#FFFFFF" strokeOpacity=".10" strokeWidth="1.5" fill="none">
        <path d="M40 400V120M480 400V120M40 120h440" />
        <path d="M40 120 260 62 480 120" />
        <path d="M96 120v-16M152 120v-30M208 120v-44M260 120V62M312 120v-44M368 120v-30M424 120v-16" />
        <path d="M40 400h440" strokeOpacity=".18" />
      </g>

      {/* Sensor mesh — instrumented plant. Links first so nodes sit on top. */}
      <g className="mesh">
        <g stroke="#7FE7FB" strokeOpacity=".28" strokeWidth="1">
          <path d="M96 176 190 148 268 186 356 152 436 184" />
          <path d="M96 176 268 186M190 148 356 152M268 186 436 184" />
          <path d="M190 148 268 118 356 152" strokeOpacity=".18" />
        </g>
        {[
          [96, 176], [190, 148], [268, 186], [356, 152], [436, 184], [268, 118],
        ].map(([cx, cy], i) => (
          <g key={i} className="node" style={{ animationDelay: `${i * 0.55}s` }}>
            <circle cx={cx} cy={cy} r="16" fill="url(#nodeGlow)" />
            <circle cx={cx} cy={cy} r="3.4" fill="#BFF2FD" />
          </g>
        ))}
      </g>

      {/* Runway rails the crane travels along. */}
      <g stroke="#FFFFFF" strokeOpacity=".30" strokeWidth="2.5">
        <path d="M56 232h408" />
      </g>

      {/* The crane: end carriages, bridge girder, trolley, hoist rope and hook block. */}
      <g className="crane">
        <rect x="150" y="222" width="34" height="14" rx="2.5" fill="#DCE8EF" fillOpacity=".92" />
        <rect x="336" y="222" width="34" height="14" rx="2.5" fill="#DCE8EF" fillOpacity=".92" />
        <rect x="150" y="206" width="220" height="17" rx="3" fill="#F0F6FA" fillOpacity=".95" />
        {/* Girder lattice: the diagonal web that makes a box girder read as one. */}
        <g stroke="#075EA8" strokeOpacity=".35" strokeWidth="1.2">
          <path d="M162 223 178 207M186 223 202 207M210 223 226 207M234 223 250 207M258 223 274 207M282 223 298 207M306 223 322 207M330 223 346 207" />
        </g>
        <rect x="236" y="196" width="52" height="13" rx="2.5" fill="#BFF2FD" />
        {/* Hoist rope + hook. The pulse runs here, where readings come from. */}
        <path className="rope" d="M262 209v96" stroke="#F0F6FA" strokeOpacity=".8" strokeWidth="1.6" />
        <path className="rope-pulse" d="M262 209v96" stroke="url(#beamGlow)" strokeWidth="3" />
        <path d="M251 305h22v13h-22z" fill="#DCE8EF" fillOpacity=".95" rx="2" />
        <path d="M262 318v10a9 9 0 1 0 9-9" stroke="#DCE8EF" strokeOpacity=".9" strokeWidth="2.5" fill="none" />
      </g>

      {/* The load: a ladle, which is what a Bokaro crane in SMS actually carries. */}
      <g fill="#FFFFFF" fillOpacity=".14" stroke="#FFFFFF" strokeOpacity=".22">
        <path d="M226 352h72l-8 44h-56z" />
        <ellipse cx="262" cy="352" rx="36" ry="6" />
      </g>

      {/* Floor line, brightest where the crane stands. */}
      <path d="M40 400h440" stroke="#7FE7FB" strokeOpacity=".35" strokeWidth="2" />
    </svg>
  )
}
